import { PatentProfileApi, type PatentProfileResponse } from '@/infrastructure/api-functions/core-drafting-api/patent-profile-api';
import type { ReferencePatent } from '../../types';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { PatentProfileRepository } from '../../services/patent-profile-repository';
import { ANCS_TYPE } from '../../enums';
import { createReferencePatent } from '../../utils';
import { I18nService } from '@telecom/shared/services/i18n';
import { BatchLoader } from '@/services/batch-loader/impls/batch-loader';

@injectable()
export class PatentProfileRepositoryImpl implements PatentProfileRepository {
    private _loader: BatchLoader<ReferencePatent, PatentProfileResponse>;

    constructor(
        @inject(PatentProfileApi)
        private readonly _patentProfileApi: PatentProfileApi,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) {
        this._loader = new BatchLoader(
            (patentNos) => this._patentProfileApi.getPatentProfile({ pns: patentNos }),
            (patentNo, resp) => this._transformData(patentNo, resp),
        );
    }

    async getPatentProfileRepository(patentNo: string): Promise<ReferencePatent> {
        return this._loader.load(patentNo);
    }

    private _transformData(patentNo: string, resp: PatentProfileResponse): ReferencePatent {
        const data = resp.find(item => item.PN === patentNo);
        let result: ReferencePatent;
        if (!data) {
            result = createReferencePatent(patentNo, this._i18nService.t('drafting.patentNotRecognized', { patentNo }));
        } else {
            result = {
                patentId: data.SOLUTION_ID,
                title: data.TITLE,
                description: data.ABST,
                patentNo: data.PN,
                iconUrl: data.ORG_INFO?.[0]?.logo ?? '',
                ancs: (data.ORG_INFO??[]).map((item) => item.normalized_name),
                ancsType: this._convertAncestorType(data.ORG_INFO?.[0]?.entity_type),
            };
        }
        return result;
    }

    private _convertAncestorType(type?: string): ANCS_TYPE {
        switch (type) {
        case 'Company':
            return ANCS_TYPE.COMPANY;
        default:
            return ANCS_TYPE.PERSON;
        }
    }
}
